import {
  buildProductosMeseroTree,
  mergeFamiliasGlobales,
  MESERO_KEY_GENERAL,
  normalizeProductosMeseroRow,
  ordenMeserosFormToGroupOrder,
  PRODUCTOS_MESERO_TITULO_GLOBAL,
  sortMergedFamiliasGlobales,
} from './productosMeseroReportModel';
import { isoADisplayDDMMAAAA } from './fechaReporte';
import { pushFiltrosSucursalesCajasCentered } from './reporteFiltrosEncabezadoExport';

/**
 * Hoja de cálculo plana con los campos solicitados:
 * familia, codigo, nombreProducto, tipoProducto, cantidad, importe, costo, costoTotal, precio, utilidad, mesero, meseroNombre
 * @param {object[]} rows filas crudas del API
 * @param {{
 *   globalSoloTotalesPorFamilia?: boolean;
 *   noGlobalAsterisco?: boolean; // true = detallado · plano (tabla plana 12 columnas)
 * }} [options]
 * @returns {{ aoa: any[][], mergeTitleRow?: object[], colWidths?: object[], excelRowStyles?: Record<number, string> }}
 */
export function buildProductosMeseroExcelAoa(rows, options = {}) {
  const {
    globalSoloTotalesPorFamilia = false,
    noGlobalAsterisco = false,
    mayorIngreso = false,
    mayorVenta = false,
    ordenMeseros = 'default',
    encabezado = null,
    fechaInicio = '',
    fechaFin = '',
    cajaInicio = null,
    cajaFin = null,
    turnoInicio = null,
    turnoFin = null,
  } = options;

  const list = (Array.isArray(rows) ? rows : [])
    .map(normalizeProductosMeseroRow)
    .filter(Boolean);

  const headers = [
    'familia',
    'codigo',
    'nombreProducto',
    'tipoProducto',
    'cantidad',
    'importe',
    'costo',
    'costoTotal',
    'precio',
    'utilidad',
    'mesero',
    'meseroNombre',
  ];

  const numCols = headers.length;
  const tituloRow = (text, cols = numCols) => {
    const r = [text];
    while (r.length < cols) r.push('');
    return r;
  };

  const aoa = [];
  const headerMergeRows = [];
  const formatFecha = (iso) =>
    (isoADisplayDDMMAAAA(String(iso || '').split('T')[0]) || String(iso || '—')).replaceAll('/', '-');
  const addHeaderBlock = (cols) => {
    const titulo = String(encabezado?.reporteTitulo || 'REPORTE PRODUCTOS VENDIDOS INCLUYENDO CORTESIAS').trim();
    const nombreComercial = String(encabezado?.nombreComercial || '').trim();
    const domicilioFiscal = String(encabezado?.domicilioFiscal || '').trim();
    const rfc = String(encabezado?.rfc || '').trim();
    const periodoTexto =
      String(encabezado?.periodoTexto || '').trim() ||
      `Del: ${formatFecha(fechaInicio)} al ${formatFecha(fechaFin)}`;
    const turnosTexto =
      String(encabezado?.turnosTexto || '').trim() || `Turnos: ${turnoInicio ?? '—'} a ${turnoFin ?? '—'}`;

    const pushCentered = (text) => {
      if (!text) return;
      const rowIdx = aoa.length;
      aoa.push(tituloRow(text, cols));
      headerMergeRows.push(rowIdx);
    };
    pushCentered(nombreComercial.toUpperCase());
    pushCentered(domicilioFiscal);
    pushCentered(rfc ? `RFC: ${rfc}` : '');
    const blank1 = aoa.length;
    aoa.push(Array(cols).fill(''));
    headerMergeRows.push(blank1);
    pushCentered(titulo.toUpperCase());
    const blank2 = aoa.length;
    aoa.push(Array(cols).fill(''));
    headerMergeRows.push(blank2);
    pushCentered(periodoTexto);
    pushFiltrosSucursalesCajasCentered(pushCentered, encabezado, {
      cajaInicio,
      cajaFin,
    });
    pushCentered(turnosTexto.startsWith('Turnos:') ? turnosTexto : `Turnos: ${turnosTexto}`);
    const blank3 = aoa.length;
    aoa.push(Array(cols).fill(''));
    headerMergeRows.push(blank3);
  };

  if (globalSoloTotalesPorFamilia) {
    const globalHeaders = ['Familia', 'Cant.', 'T.Costo', 'Importe', 'Utilidad'];
    const globalCols = globalHeaders.length;
    const globalBlankRow = () => Array(globalCols).fill('');
    const merges = [];

    addHeaderBlock(globalCols);
    for (const i of headerMergeRows) {
      merges.push({ s: { r: i, c: 0 }, e: { r: i, c: globalCols - 1 } });
    }
    aoa.push(tituloRow(PRODUCTOS_MESERO_TITULO_GLOBAL, globalCols));
    merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: globalCols - 1 } });
    aoa.push(globalBlankRow());

    const meseroRowOrder = mayorIngreso ? 'importeDesc' : mayorVenta ? 'cantidadDesc' : 'default';
    const meseroGroupOrder = ordenMeserosFormToGroupOrder(ordenMeseros);
    const tree = buildProductosMeseroTree(list, {
      meseroRowOrder,
      meseroGroupOrder,
      sinApartado: true,
    });
    const soloPorFamilia = tree.length === 1 && tree[0]?.key === MESERO_KEY_GENERAL;
    const totalGeneral = { cantidad: 0, totalCosto: 0, importe: 0, utilidad: 0 };

    for (const mesero of tree) {
      if (!soloPorFamilia) {
        const rowIdx = aoa.length;
        aoa.push(tituloRow(`MESERO: ${String(mesero.label || '').toUpperCase()}`, globalCols));
        merges.push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: globalCols - 1 } });
      }

      aoa.push(globalHeaders);

      const familiasGlobal = sortMergedFamiliasGlobales(
        mergeFamiliasGlobales(mesero),
        meseroRowOrder
      );
      for (const fam of familiasGlobal) {
        aoa.push([
          String(fam.familia ?? ''),
          Number(fam?.totals?.cantidad ?? 0),
          Number(fam?.totals?.totalCosto ?? 0),
          Number(fam?.totals?.importe ?? 0),
          Number(fam?.totals?.utilidad ?? 0),
        ]);
      }

      aoa.push([
        soloPorFamilia ? 'TOTAL REPORTE' : 'TOTAL MESERO',
        Number(mesero?.meseroTotals?.cantidad ?? 0),
        Number(mesero?.meseroTotals?.totalCosto ?? 0),
        Number(mesero?.meseroTotals?.importe ?? 0),
        Number(mesero?.meseroTotals?.utilidad ?? 0),
      ]);
      aoa.push(globalBlankRow());

      totalGeneral.cantidad += Number(mesero?.meseroTotals?.cantidad ?? 0);
      totalGeneral.totalCosto += Number(mesero?.meseroTotals?.totalCosto ?? 0);
      totalGeneral.importe += Number(mesero?.meseroTotals?.importe ?? 0);
      totalGeneral.utilidad += Number(mesero?.meseroTotals?.utilidad ?? 0);
    }

    aoa.push([
      'TOTAL GENERAL',
      Number(totalGeneral.cantidad || 0),
      Number(totalGeneral.totalCosto || 0),
      Number(totalGeneral.importe || 0),
      Number(totalGeneral.utilidad || 0),
    ]);

    return {
      aoa,
      mergeTitleRow: merges,
      colWidths: [
        { wch: 38 }, // Familia
        { wch: 12 }, // Cant.
        { wch: 16 }, // T.Costo
        { wch: 16 }, // Importe
        { wch: 16 }, // Utilidad
      ],
    };
  }

  /** Detallado · Ejecutivo: MESERO bar, APARTADO:, una cabecera de tabla por apartado, familias y productos en columnas 8. */
  if (!globalSoloTotalesPorFamilia && !noGlobalAsterisco) {
    const detailCols = 8;
    const detailHeaders = [
      'Código',
      'Nombre',
      'Cant.',
      'Costo',
      'T.Costo',
      'Precio',
      'Importe',
      'Utilidad',
    ];
    const blankRow = () => Array(detailCols).fill('');
    const merges = [];
    /** @type {Record<number, string>} */
    const excelRowStyles = {};

    addHeaderBlock(detailCols);
    for (const i of headerMergeRows) {
      merges.push({ s: { r: i, c: 0 }, e: { r: i, c: detailCols - 1 } });
    }

    const pushMergedTitle = (text, role) => {
      const rowIdx = aoa.length;
      aoa.push(tituloRow(text, detailCols));
      merges.push({ s: { r: rowIdx, c: 0 }, e: { r: rowIdx, c: detailCols - 1 } });
      if (role) excelRowStyles[rowIdx] = role;
    };

    const meseroRowOrder = mayorIngreso ? 'importeDesc' : mayorVenta ? 'cantidadDesc' : 'default';
    const meseroGroupOrder = ordenMeserosFormToGroupOrder(ordenMeseros);
    const tree = buildProductosMeseroTree(list, {
      meseroRowOrder,
      meseroGroupOrder,
      sinApartado: false,
    });
    const soloPorFamilia = tree.length === 1 && tree[0]?.key === MESERO_KEY_GENERAL;
    const totalGeneral = { cantidad: 0, totalCosto: 0, importe: 0, utilidad: 0 };

    for (const mesero of tree) {
      if (!soloPorFamilia) {
        pushMergedTitle(`MESERO: ${String(mesero.label || '').toUpperCase()}`, 'meseroBar');
      }

      for (const apartado of mesero.apartados) {
        pushMergedTitle(`APARTADO: ${String(apartado.label || '').toUpperCase()}`, 'apartadoLabel');

        aoa.push(detailHeaders);

        for (const fam of apartado.familias) {
          const famIdx = aoa.length;
          aoa.push(tituloRow(String(fam.familia || '').toUpperCase(), detailCols));
          merges.push({ s: { r: famIdx, c: 0 }, e: { r: famIdx, c: detailCols - 1 } });
          excelRowStyles[famIdx] = 'familiaRow';

          for (const r of fam.rows) {
            aoa.push([
              r.codigo,
              r.nombre,
              Number(r.cantidad ?? 0),
              Number(r.costo ?? 0),
              Number(r.totalCosto ?? 0),
              Number(r.precio ?? 0),
              Number(r.importe ?? 0),
              Number(r.utilidad ?? 0),
            ]);
          }

          const ft = fam.totals || {};
          const totalIdx = aoa.length;
          aoa.push([
            'Total',
            '',
            Number(ft.cantidad ?? 0),
            '',
            Number(ft.totalCosto ?? 0),
            '',
            Number(ft.importe ?? 0),
            Number(ft.utilidad ?? 0),
          ]);
          excelRowStyles[totalIdx] = 'totalFamilia';
        }

        aoa.push(blankRow());
      }

      const mt = mesero.meseroTotals || {};
      aoa.push([
        'TOTAL MESERO',
        '',
        Number(mt.cantidad ?? 0),
        '',
        Number(mt.totalCosto ?? 0),
        '',
        Number(mt.importe ?? 0),
        Number(mt.utilidad ?? 0),
      ]);
      aoa.push(blankRow());

      totalGeneral.cantidad += Number(mt.cantidad ?? 0);
      totalGeneral.totalCosto += Number(mt.totalCosto ?? 0);
      totalGeneral.importe += Number(mt.importe ?? 0);
      totalGeneral.utilidad += Number(mt.utilidad ?? 0);
    }

    aoa.push([
      'TOTAL GENERAL',
      '',
      Number(totalGeneral.cantidad || 0),
      '',
      Number(totalGeneral.totalCosto || 0),
      '',
      Number(totalGeneral.importe || 0),
      Number(totalGeneral.utilidad || 0),
    ]);

    return {
      aoa,
      mergeTitleRow: merges,
      colWidths: [
        { wch: 14 },
        { wch: 42 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
      ],
      excelRowStyles,
    };
  }

  /** Detallado · Plano: una sola tabla con 12 columnas; cada fila repite familia y mesero (sin agrupar). */
  if (noGlobalAsterisco && !globalSoloTotalesPorFamilia) {
    const merges = [];

    addHeaderBlock(numCols);
    for (const i of headerMergeRows) {
      merges.push({ s: { r: i, c: 0 }, e: { r: i, c: numCols - 1 } });
    }

    aoa.push(headers);

    const upper = (s) => String(s ?? '').trim().toUpperCase();

    const totalsPlano = {
      cantidad: 0,
      importe: 0,
      totalCosto: 0,
      utilidad: 0,
    };

    for (const r of list) {
      let tipoStr = r.tipoProducto || '';
      if (!tipoStr) {
        if (r.esBebidas) tipoStr = 'BEBIDA';
        else tipoStr = 'ALIMENTO';
      }
      totalsPlano.cantidad += Number(r.cantidad ?? 0);
      totalsPlano.importe += Number(r.importe ?? 0);
      totalsPlano.totalCosto += Number(r.totalCosto ?? 0);
      totalsPlano.utilidad += Number(r.utilidad ?? 0);
      aoa.push([
        upper(r.familia),
        r.codigo,
        upper(r.nombre),
        upper(tipoStr),
        Number(r.cantidad ?? 0),
        Number(r.importe ?? 0),
        Number(r.costo ?? 0),
        Number(r.totalCosto ?? 0),
        Number(r.precio ?? 0),
        Number(r.utilidad ?? 0),
        r.meseroId,
        upper(r.meseroNombre),
      ]);
    }

    aoa.push([
      'TOTAL GENERAL',
      '',
      '',
      '',
      totalsPlano.cantidad,
      totalsPlano.importe,
      '',
      totalsPlano.totalCosto,
      '',
      totalsPlano.utilidad,
      '',
      '',
    ]);

    return {
      aoa,
      mergeTitleRow: merges,
      colWidths: [
        { wch: 18 }, // familia
        { wch: 12 }, // codigo
        { wch: 40 }, // nombreProducto
        { wch: 15 }, // tipoProducto
        { wch: 10 }, // cantidad
        { wch: 12 }, // importe
        { wch: 12 }, // costo
        { wch: 12 }, // costoTotal
        { wch: 12 }, // precio
        { wch: 12 }, // utilidad
        { wch: 10 }, // mesero
        { wch: 25 }, // meseroNombre
      ],
    };
  }

  addHeaderBlock(numCols);
  aoa.push(headers);

  const upperFlat = (s) => String(s ?? '').trim().toUpperCase();

  const totalsFlat = {
    cantidad: 0,
    importe: 0,
    totalCosto: 0,
    utilidad: 0,
  };

  for (const r of list) {
    let tipoStr = r.tipoProducto || '';
    if (!tipoStr) {
      if (r.esBebidas) tipoStr = 'BEBIDA';
      else tipoStr = 'ALIMENTO';
    }
    totalsFlat.cantidad += Number(r.cantidad ?? 0);
    totalsFlat.importe += Number(r.importe ?? 0);
    totalsFlat.totalCosto += Number(r.totalCosto ?? 0);
    totalsFlat.utilidad += Number(r.utilidad ?? 0);
    aoa.push([
      upperFlat(r.familia),
      r.codigo,
      upperFlat(r.nombre),
      upperFlat(tipoStr),
      r.cantidad,
      r.importe,
      r.costo,
      r.totalCosto,
      r.precio,
      r.utilidad,
      r.meseroId,
      upperFlat(r.meseroNombre),
    ]);
  }

  aoa.push([
    'TOTAL GENERAL',
    '',
    '',
    '',
    totalsFlat.cantidad,
    totalsFlat.importe,
    '',
    totalsFlat.totalCosto,
    '',
    totalsFlat.utilidad,
    '',
    '',
  ]);

  return {
    aoa,
    mergeTitleRow: headerMergeRows.map((i) => ({
      s: { r: i, c: 0 },
      e: { r: i, c: numCols - 1 },
    })),
    colWidths: [
      { wch: 18 }, // familia
      { wch: 12 }, // codigo
      { wch: 40 }, // nombreProducto
      { wch: 15 }, // tipoProducto
      { wch: 10 }, // cantidad
      { wch: 12 }, // importe
      { wch: 12 }, // costo
      { wch: 12 }, // costoTotal
      { wch: 12 }, // precio
      { wch: 12 }, // utilidad
      { wch: 10 }, // mesero
      { wch: 25 }, // meseroNombre
    ],
  };
}
